import React, { useState, useEffect } from "react"
import { SettingsTitle } from "../components/ui/settings/settings-title"
import { AccountPreferences } from "../components/ui/settings/account-preferences"
import { NotificationSettings } from "../components/ui/notification-settings"
import { PrivacyControls } from "../components/ui/settings/privacy-controls"
import { AppPreferences } from "../components/ui/settings/app-preferences"
import { Sidebar } from "../components/ui/sidebar"
import { UniversalSearch } from "../components/ui/universal-search"
import ProtectedRoute from "../components/ProtectedRoute"
import { useNavigate } from "react-router"
import { StorageService } from "../hooks/services/storageService"
import { AuthStore } from "../hooks/stores/AuthStore"
import { RotateCcw, FlaskConical, ChevronRight, Trash2 } from "lucide-react"

const IS_PROTO = import.meta.env.VITE_APP_MODE === "prototype"

function PrototypeTools() {
  const navigate = useNavigate()
  const [confirm, setConfirm] = useState(null) // 'onboarding' | 'full'

  const resetOnboarding = () => {
    const user = AuthStore.getState().currentUser
    if (!user) return
    const students = StorageService.read("students")
    const idx = students.findIndex(
      (s) => s.userId === user._id || s._id === user._id || s.email === user.email
    )
    if (idx === -1) return
    students[idx] = {
      ...students[idx],
      metadata: { ...(students[idx].metadata ?? {}), onboardingCompleted: false },
    }
    StorageService.write("students", students)
    // Clear splash so it shows again
    sessionStorage.removeItem("splashShown")
    navigate("/onboarding")
  }

  const resetFull = () => {
    const user = AuthStore.getState().currentUser
    if (!user) return
    const students = StorageService.read("students")
    const idx = students.findIndex(
      (s) => s.userId === user._id || s._id === user._id || s.email === user.email
    )
    if (idx === -1) return
    const base = students[idx]
    students[idx] = {
      ...base,
      firstName: "",
      lastName: "",
      avatar: null,
      academicResults: [],
      traitProfile: null,
      traitEvents: [],
      careerSuggestions: [],
      selectedBroadPaths: [],
      preferences: {
        ...(base.preferences ?? {}),
        stream: "general",
        classes: [],
        country: "",
        examBody: "",
        classLevel: "",
      },
      metadata: { onboardingCompleted: false },
    }
    StorageService.write("students", students)
    sessionStorage.removeItem("splashShown")
    navigate("/onboarding")
  }

  return (
    <div className="bg-white border border-amber-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-amber-50 border-b border-amber-200">
        <div className="w-8 h-8 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
          <FlaskConical className="w-4 h-4 text-amber-600" />
        </div>
        <div>
          <p className="text-sm font-bold text-amber-900">Prototype Tools</p>
          <p className="text-xs text-amber-600">Only visible in prototype mode</p>
        </div>
      </div>

      <div className="p-5 space-y-3">
        {/* Reset onboarding */}
        {confirm === "onboarding" ? (
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 space-y-3">
            <p className="text-sm font-semibold text-orange-900">
              Re-run onboarding for this account?
            </p>
            <p className="text-xs text-orange-700">
              Clears <code className="bg-orange-100 px-1 rounded">onboardingCompleted</code> flag and redirects to <code className="bg-orange-100 px-1 rounded">/onboarding</code>. Profile data (name, avatar, stream) is kept.
            </p>
            <div className="flex gap-2">
              <button
                onClick={resetOnboarding}
                className="flex-1 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg transition-all active:scale-95"
              >
                Yes, restart onboarding
              </button>
              <button
                onClick={() => setConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setConfirm("onboarding")}
            className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50 text-left transition-all group"
          >
            <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
              <RotateCcw className="w-4 h-4 text-indigo-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">Reset Onboarding</p>
              <p className="text-xs text-slate-500">Re-run the welcome wizard for this account</p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-500 transition-colors" />
          </button>
        )}

        {/* Full reset */}
        {confirm === "full" ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-3">
            <p className="text-sm font-semibold text-red-900">
              Full profile reset — are you sure?
            </p>
            <p className="text-xs text-red-700">
              Clears name, avatar, stream, classes, academic results, trait profile, and career suggestions. Cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={resetFull}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-all active:scale-95"
              >
                Yes, reset everything
              </button>
              <button
                onClick={() => setConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setConfirm("full")}
            className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 bg-white hover:border-red-200 hover:bg-red-50 text-left transition-all group"
          >
            <div className="w-8 h-8 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
              <Trash2 className="w-4 h-4 text-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">Full Profile Reset</p>
              <p className="text-xs text-slate-500">Clear all profile data and restart from scratch</p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-red-500 transition-colors" />
          </button>
        )}
      </div>
    </div>
  )
}

const Settings = () => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleSearch = (query) => {
    console.log("Universal search for:", query)
  }

  return (
   <ProtectedRoute>
     <div className="flex min-h-screen bg-slate-200">
        {/* Sidebar - Handles its own mobile logic internally */}
        <Sidebar />

        

        {/* Universal Search Component */}
        <UniversalSearch onSearch={handleSearch} />

        {/* Main Content */}
        <main
          className="
            flex-1 p-3 sm:p-4 md:p-6 lg:p-8 w-full min-h-screen overflow-y-auto
            transition-all duration-300 ease-in-out
            lg:ml-64  /* ensures sidebar space is reserved on desktop */
          "
        >
          {/* Mobile Header Spacing for Fixed Hamburger (from Sidebar) */}
          <div className="md:hidden h-16 mb-4"></div>

          {/* Mobile Header Content */}
          <div className="md:hidden flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-900">Settings</h1>
              <p className="text-sm text-slate-600">Manage your preferences</p>
            </div>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Desktop Header */}
            <div className="hidden md:block">
              <SettingsTitle />
            </div>

            {/* Settings Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12">
              <AccountPreferences />
              <NotificationSettings />
              <PrivacyControls />
              <AppPreferences />
            </div>

            {IS_PROTO && (
              <div className="mb-8">
                <PrototypeTools />
              </div>
            )}
          </div>
        </main>
      </div>
   </ProtectedRoute>
  )
}

export default Settings